import { useQuery } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_BASE || '';

// Backend DTO from /api/wait-times (QueueTimeLatestDto)
interface BackendQueueTimeDto {
  attractionName: string;
  parkName: string;
  entityType: string;
  waitTimeMinutes: number | null;
  status: string | null;
  timestamp: string | null;
  rating: number | null;
  priority: string | null;
  active: boolean;
}

// Frontend model used by WaitTimesPage
export interface AdminAttraction {
  id: string;
  name: string;
  parkName: string;
  entityType: string;
  rating: number | null;
  priority: string | null;
  waitTimeMinutes: number | null;
  queueStatus: string | null;
  minutesSinceUpdate: number | null;
}

export interface AdminAttractionsParams {
  parkName?: string;
  entityType?: string;
  sortBy?: 'name' | 'rating' | 'priority';
}

// Map backend DTO to frontend AdminAttraction model
function mapAttraction(dto: BackendQueueTimeDto): AdminAttraction {
  // Derive minutesSinceUpdate from backend timestamp
  let minutesSinceUpdate: number | null = null;
  if (dto.timestamp) {
    const now = Date.now();
    const then = new Date(dto.timestamp).getTime();
    minutesSinceUpdate = Math.floor((now - then) / 60000);
  }

  return {
    id: dto.attractionName, // No unique ID from backend — use name as key
    name: dto.attractionName,
    parkName: dto.parkName,
    entityType: dto.entityType,
    rating: dto.rating,
    priority: dto.priority,
    waitTimeMinutes: dto.waitTimeMinutes,
    queueStatus: dto.status,
    minutesSinceUpdate,
  };
}

function useAdminAttractionsBase(params: AdminAttractionsParams = {}) {
  // Build query params for the new /api/wait-times endpoint
  const searchParams = new URLSearchParams();
  if (params.parkName) {
    searchParams.set('parkNames', params.parkName);
  }
  if (params.entityType) {
    searchParams.set('entityType', params.entityType);
  }
  if (params.sortBy) {
    searchParams.set('sortBy', params.sortBy);
  }
  // Always send default filter to ensure only ATTRACTION + SHOW are returned
  searchParams.set('defaultFilter', 'true');

  const queryStr = searchParams.toString();
  const url = `${API_BASE}/api/wait-times${queryStr ? '?' + queryStr : ''}`;

  return useQuery({
    queryKey: ['wait-times', url],
    queryFn: async () => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const rawData = await response.json() as BackendQueueTimeDto[];
      // Map and sort client-side for consistency
      const mapped = rawData.map(mapAttraction);
      // Multi-key sort: primary (user-selected) → priority → rating → name
      const priorityOrder: Record<string, number> = { HIGH: 1, MEDIUM: 2, LOW: 3 };
      return mapped.sort((a, b) => {
        const primary = params.sortBy || 'priority';
        if (primary === 'priority') {
          const pa = priorityOrder[a.priority ?? 'MEDIUM'] ?? 2;
          const pb = priorityOrder[b.priority ?? 'MEDIUM'] ?? 2;
          if (pa !== pb) return pa - pb;
        }
        if (primary === 'rating') {
          const ra = a.rating ?? -Infinity;
          const rb = b.rating ?? -Infinity;
          if (ra !== rb) return rb - ra; // higher rating first
        }
        if (primary === 'name') {
          if (a.name !== b.name) return a.name.localeCompare(b.name);
        }
        // Tiebreakers: priority → rating → name (always)
        const pa = priorityOrder[a.priority ?? 'MEDIUM'] ?? 2;
        const pb = priorityOrder[b.priority ?? 'MEDIUM'] ?? 2;
        if (pa !== pb) return pa - pb;
        const ra = a.rating ?? -Infinity;
        const rb = b.rating ?? -Infinity;
        if (ra !== rb) return rb - ra;
        return a.name.localeCompare(b.name);
      });
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useAdminAttractions(params: AdminAttractionsParams = {}) {
  const base = useAdminAttractionsBase(params);

  // Client-side filter for entityType (park filter already sent server-side)
  const data = params.entityType
    ? (base.data ?? []).filter(a => a.entityType.toLowerCase() === params.entityType!.toLowerCase())
    : base.data;

  return {
    ...base,
    data,
    attractions: data,
    // Parks derived from frontend data — no park info in the DTO
    parks: [],
    entityTypes: data ? Array.from(new Set(data.map(a => a.entityType))).sort() : [],
  };
}

export function useEntityTypeOptions() {
  return [
    { value: '', label: 'All types' },
    { value: 'ATTRACTION', label: 'Attraction' },
    { value: 'SHOW', label: 'Show' },
  ];
}
